<template lang="pug">
  .advanced-conversation-filter(:class="{ 'is-expanded': isExpanded }")
    .no-search(v-if="showBackArrow")
      .back-to-conversation(@click="$emit('back')")
        i.iq-ico-arrow
        span {{ $t('conversation_tab.left_panel.advanced_filter.back_to_conversations') }}

    template(v-else)
      .status-filter-container(v-if="showFilterChip")
        aiq-checkbox-group(:model-value="selectedStatusFilters" size="small")
          aiq-tooltip(effect="dark", :content="$t('conversation_tab.left_panel.advanced_filter.status_filters.unread_tooltip')", placement="top", :show-after="1000")
            aiq-checkbox-button(label="unread"
                                value="unread"
                                @change="onStatusFilterChange")
              | {{ $t('conversation_tab.left_panel.advanced_filter.status_filters.unread') }}
          aiq-tooltip(effect="dark", :content="$t('conversation_tab.left_panel.advanced_filter.status_filters.watch_list_tooltip')", placement="top", :show-after="1000")
            aiq-checkbox-button(label="starred"
                                value="starred"
                                @change="onStatusFilterChange")
              | {{ $t('conversation_tab.left_panel.advanced_filter.status_filters.watch_list') }} &nbsp; {{ counters.starred }}

      .filter-block(:class="{ 'is-expanded': isExpanded }")
        .filter-header(@click="toggleExpanded" :class="{ expanded: isExpanded }")
          span.filter-header-title {{ $t('conversation_tab.left_panel.advanced_filter.search_and_filter', { count: filterCount }) }}
          fa-icon.filter-header-icon(:icon="['fas', 'chevron-down']" :class="{ expanded: isExpanded }")

        .filter-applied(v-if="!isExpanded")
          .applied-filters-empty(v-if="!hasAppliedFilters")
            | {{ $t('conversation_tab.left_panel.advanced_filter.active_filters_none') }}
          template(v-else)
            .applied-filters-header
              span.applied-filters-title {{ $t('conversation_tab.left_panel.advanced_filter.active_filters') }}
              a.applied-filters-link(
                href="#"
                @click.prevent.stop="expandFilterPanel") {{ $t('conversation_tab.left_panel.advanced_filter.edit_filters') }}
              a.applied-filters-link(
                href="#"
                @click.prevent.stop="clearAllAppliedFilters") {{ $t('conversation_tab.left_panel.advanced_filter.clear_all') }}
            .applied-filter-chip(
              v-for="chip in appliedFilterChips"
              :key="chip.key")
              span.applied-filter-chip-text
                span.applied-filter-chip-label {{ chip.label }}:
                |  {{ chip.value }}
              button.applied-filter-chip-close(
                type="button"
                @click.stop="removeAppliedFilter(chip.key)")
                fa-icon(:icon="['fas', 'times']")

        .filter-form(v-if="isExpanded")
          .filter-form-fields
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.preset_filters') }}
              aiq-select(
                v-model="selectedPresets"
                multiple
                clearable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="option in availablePresetOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.search') }}
              .filter-field-row
                aiq-select(
                  v-model="filter.searchType"
                  size="small"
                  :suffix-icon="filterSelectSuffixIcon"
                  class="filter-select")
                  aiq-option(
                    v-for="option in searchTypeOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value")
              .filter-field-row
                aiq-input(
                  v-model="filter.searchValue"
                  size="small"
                  :placeholder="$t('conversation_tab.left_panel.advanced_filter.search_placeholder')"
                  @keyup.enter="onSearchEnter")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.conversation_created') }}
              .filter-field-row
                aiq-date-picker(
                  v-model="filter.dateRange.start_date"
                  @change="onDateStartChange"
                  size="small"
                  type="datetime"
                  :placeholder="$t('conversation_tab.left_panel.advanced_filter.start_date_placeholder')"
                  :format="conversationCreatedDateTimeFormat"
                  popper-class="messages-search__date-picker-popup"
                  :prefix-icon="datePickerPrefixIcon"
                  :clear-icon="Icon.CircleCloseFilled"
                  :disabled-date="disableFutureDate"
                  class="conversations-date-picker")
              .filter-field-row
                aiq-date-picker(
                  v-model="filter.dateRange.end_date"
                  @change="onDateEndChange"
                  size="small"
                  type="datetime"
                  :placeholder="$t('conversation_tab.left_panel.advanced_filter.end_date_placeholder')"
                  popper-class="messages-search__date-picker-popup"
                  :format="conversationCreatedDateTimeFormat"
                  :default-time="endDatePickerDefaultTime"
                  :prefix-icon="datePickerPrefixIcon"
                  :clear-icon="Icon.CircleCloseFilled"
                  :disabled-date="disableFutureDate"
                  class="conversations-date-picker")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.tag') }}
              aiq-select(
                v-model="filter.tag_id"
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="tag in tagsList"
                  :key="tag.id"
                  :label="tag.name"
                  :value="tag.id")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.category') }}
              aiq-select(
                v-model="filter.category_id"
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="category in categoriesList"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.segments') }}
              aiq-select(
                v-model="filter.segment_ids"
                multiple
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="segment in segmentsList"
                  :key="segment.id"
                  :label="segment.name"
                  :value="segment.id")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.agent') }}
              aiq-select(
                v-model="filter.agent_id"
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="agent in agents"
                  :key="agent.id"
                  :label="agent.fullName"
                  :value="agent.id")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.team') }}
              aiq-select(
                v-model="filter.team_id"
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="team in teams"
                  :key="team.id"
                  :label="team.name"
                  :value="team.id")
            .filter-field
              label.filter-field-label {{ $t('conversation_tab.left_panel.advanced_filter.last_feedback_score') }}
              aiq-select(
                v-model="filter.last_feedback_score"
                clearable
                filterable
                size="small"
                :placeholder="$t('conversation_tab.left_panel.advanced_filter.select_placeholder')"
                :clear-icon="Icon.CircleCloseFilled"
                :suffix-icon="filterSelectSuffixIcon"
                :reserve-keyword="false"
                class="filter-select")
                aiq-option(
                  v-for="option in scoreFilterOptions"
                  :key="option.value"
                  :label="option.name"
                  :value="option.value")
          .filter-actions
            aiq-button.filter-action-button(
              size="small"
              @click.stop="clearFilter") {{ $t('conversation_tab.left_panel.advanced_filter.clear') }}
            aiq-button.filter-action-button(
              size="small"
              type="primary"
              @click.stop="applyFilter"
              :disabled="isFilterUnchanged") {{ $t('conversation_tab.left_panel.advanced_filter.apply') }}
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import dayjs from '@/localPlugins/dayjs';
import { adjustUrlArray, alphabetSort, capToNow, createFontAwesomeIcon, disableFutureDate } from '@/libs';
import { AGENT_LIMIT, TEAM_LIMIT, CATEGORY_LIMIT, TAG_LIMIT } from '@/constants/pagination';
import {
  PRESET_FILTER_KEYS,
  SEARCH_TYPES,
  DEFAULT_SEARCH,
  DEFAULT_FILTER,
  coerceFilterState,
  parseFilterFromUrlQuery,
  buildServerPayload,
  buildUrlQueryFromFilterState,
  hasAdvancedFilterQueryParams,
  isAppliedFilterEmpty,
  stripAdvancedQueryParams,
  sanitizeAdvancedQueryParams,
  adjustAdvancedUrlArrays,
  areAppliedFiltersEqual,
  getActivePresetKeys,
  hasSearchValue,
  areFiltersEqual,
  isFilterFilled,
  findFilterItemById,
} from './advancedConversationFilterQuery';

const DATE_TIME_PICKER_ICON = createFontAwesomeIcon('calendar-clock', { name: 'DateTimePickerIcon' });

const getScoreFilterOptions = (surveyType) => {
  let scoreRange = [];
  if (surveyType === 'csat') {
    // Score range: 1 ~ 5
    scoreRange = [...Array(5).keys()].map((i) => i + 1); // eslint-disable-line
  } else if (surveyType === 'nps') {
    // Score range: 0 ~ 10
    scoreRange = [...Array(11).keys()]; // eslint-disable-line
  }
  return scoreRange.map((index) => ({ value: index, name: String(index) }));
};

const CONVERSATION_CREATED_DATE_TIME_FORMAT = 'YYYY/MM/DD hh:mm a';

const toIsoString = (date) => dayjs.utc(new Date(date)).toISOString();

const END_DATE_PICKER_DEFAULT_TIME = (() => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0); // eslint-disable-line no-magic-numbers
})();

const formatDateValue = (value) => {
  if (!value) return '';
  return dayjs(value).format(CONVERSATION_CREATED_DATE_TIME_FORMAT);
};

export default {
  name: 'AdvancedConversationFilter',
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
    appliedFilter: {
      type: Object,
      default: () => ({}),
    },
    selectedStatusFilters: {
      type: Array,
      default: () => [],
    },
    showBackArrow: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'applyFilter', 'select', 'back'],
  data() {
    return {
      conversationCreatedDateTimeFormat: CONVERSATION_CREATED_DATE_TIME_FORMAT,
      endDatePickerDefaultTime: END_DATE_PICKER_DEFAULT_TIME,
      isExpanded: false,
      filter: cloneDeep(DEFAULT_FILTER),
      committedFilter: cloneDeep(DEFAULT_FILTER),
      skipRouteFilterApply: false,
      filterListsLoaded: false,
    };
  },
  computed: {
    ...mapState({
      selectedTab: state => state.conversations.selectedTab,
      counters: state => state.conversations.counters,
      tagsListState: state => state.tags.tags || [],
      categoriesListState: state => state.categories.categories || [],
      surveyType: state => state.metrics.survey.surveyType,
    }),
    ...mapGetters({
      segmentsList: 'configs/segments',
      agents: 'agents/sortedList',
      teams: 'teams/sortedTeams',
    }),
    tagsList() {
      return [...this.tagsListState].sort(alphabetSort);
    },
    categoriesList() {
      return [...this.categoriesListState].sort(alphabetSort);
    },
    filterSelectSuffixIcon() {
      return this.Icon.ArrowDown;
    },
    datePickerPrefixIcon() {
      return DATE_TIME_PICKER_ICON;
    },
    scoreFilterOptions() {
      return getScoreFilterOptions(this.surveyType);
    },
    availablePresetOptions() {
      return PRESET_FILTER_KEYS
        .filter((key) => key !== 'i_am_primary' || this.shouldShowPrimaryAgent)
        .map((key) => ({
          value: key,
          label: this.$t(`conversation_tab.left_panel.advanced_filter.presets.${key}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    searchTypeOptions() {
      return SEARCH_TYPES.map((value) => ({
        value,
        label: this.$t(`conversation_tab.left_panel.advanced_filter.search_types.${value}`),
      }));
    },
    selectedPresets: {
      get() {
        return getActivePresetKeys(this.filter, this.shouldShowPrimaryAgent);
      },
      set(values) {
        const selectedValues = values || [];
        PRESET_FILTER_KEYS.forEach((key) => {
          if (key === 'i_am_primary' && !this.shouldShowPrimaryAgent) {
            this.filter[key] = false;
            return;
          }
          this.filter[key] = selectedValues.includes(key);
        });
      },
    },
    shouldShowPrimaryAgent() {
      return this.selectedTab === 'mine';
    },
    showFilterChip() {
      return !['all', 'team', 'queue'].includes(this.selectedTab);
    },
    filterCount() {
      let count = 0;
      if (getActivePresetKeys(this.committedFilter, this.shouldShowPrimaryAgent).length > 0) {
        count += 1;
      }
      if (hasSearchValue(this.committedFilter)) {
        count += 1;
      }
      if (this.committedFilter.tag_id) {
        count += 1;
      }
      if (this.committedFilter.category_id) {
        count += 1;
      }
      if (this.committedFilter.agent_id) {
        count += 1;
      }
      if (this.committedFilter.team_id) {
        count += 1;
      }
      if (this.committedFilter.last_feedback_score !== null
        && this.committedFilter.last_feedback_score !== undefined) {
        count += 1;
      }
      if ((this.committedFilter.segment_ids || []).length > 0) {
        count += 1;
      }
      if (this.committedFilter.dateRange.start_date || this.committedFilter.dateRange.end_date) {
        count += 1;
      }
      return count;
    },
    hasAppliedFilters() {
      return this.filterCount > 0;
    },
    appliedPresetLabels() {
      return getActivePresetKeys(this.committedFilter, this.shouldShowPrimaryAgent)
        .map((key) => this.$t(`conversation_tab.left_panel.advanced_filter.presets.${key}`));
    },
    formattedAppliedTag() {
      if (!this.committedFilter.tag_id) return '';

      const tag = this.tagsList.find((item) => String(item.id) === String(this.committedFilter.tag_id));
      return tag?.name || '';
    },
    formattedAppliedCategory() {
      if (!this.committedFilter.category_id) return '';

      const category = this.categoriesList.find((item) => String(item.id) === String(this.committedFilter.category_id));
      return category?.name || '';
    },
    formattedAppliedSegments() {
      const segmentNames = (this.committedFilter.segment_ids || [])
        .map((segmentId) => this.segmentsList.find((item) => String(item.id) === String(segmentId))?.name)
        .filter(Boolean);

      return segmentNames.join(', ');
    },
    formattedAppliedAgent() {
      if (!this.committedFilter.agent_id) return '';

      const agent = findFilterItemById(this.agents, this.committedFilter.agent_id);
      return agent?.fullName || this.committedFilter.agent_id || '';
    },
    formattedAppliedTeam() {
      if (!this.committedFilter.team_id) return '';

      const team = findFilterItemById(this.teams, this.committedFilter.team_id);
      return team?.name || '';
    },
    formattedAppliedLastFeedbackScore() {
      const { last_feedback_score: score } = this.committedFilter;
      if (score === null || score === undefined) return '';

      return String(score);
    },
    formattedDateRange() {
      const { start_date: startDate, end_date: endDate } = this.committedFilter.dateRange;
      const start = formatDateValue(startDate);
      const end = formatDateValue(endDate);

      if (start && end) return `${start} - ${end}`;
      if (start) return start;
      return end;
    },
    isFilterUnchanged() {
      return areFiltersEqual(this.filter, this.committedFilter);
    },
    isDraftFilled() {
      return isFilterFilled(this.filter, this.shouldShowPrimaryAgent);
    },
    appliedFilterChips() {
      const chips = [];
      const label = (key) => this.$t(`conversation_tab.left_panel.advanced_filter.${key}`);

      if (this.appliedPresetLabels.length) {
        chips.push({
          key: 'presets',
          label: label('preset_filters'),
          value: this.appliedPresetLabels.join(', '),
        });
      }
      if (hasSearchValue(this.committedFilter)) {
        const { searchType, searchValue } = this.committedFilter;
        const searchLabel = searchType === 'all'
          ? label('search')
          : this.$t(`conversation_tab.left_panel.advanced_filter.search_types.${searchType}`);

        chips.push({
          key: 'search',
          label: searchLabel,
          value: searchValue.trim(),
        });
      }
      if (this.committedFilter.dateRange.start_date || this.committedFilter.dateRange.end_date) {
        chips.push({
          key: 'dateRange',
          label: label('conversation_created'),
          value: this.formattedDateRange,
        });
      }
      if (this.formattedAppliedTag) {
        chips.push({
          key: 'tag',
          label: label('tag'),
          value: this.formattedAppliedTag,
        });
      }
      if (this.formattedAppliedCategory) {
        chips.push({
          key: 'category',
          label: label('category'),
          value: this.formattedAppliedCategory,
        });
      }
      if (this.formattedAppliedSegments) {
        chips.push({
          key: 'segments',
          label: label('segments'),
          value: this.formattedAppliedSegments,
        });
      }
      if (this.formattedAppliedAgent) {
        chips.push({
          key: 'agent',
          label: label('agent'),
          value: this.formattedAppliedAgent,
        });
      }
      if (this.formattedAppliedTeam) {
        chips.push({
          key: 'team',
          label: label('team'),
          value: this.formattedAppliedTeam,
        });
      }
      if (this.formattedAppliedLastFeedbackScore) {
        chips.push({
          key: 'lastFeedbackScore',
          label: label('last_feedback_score'),
          value: this.formattedAppliedLastFeedbackScore,
        });
      }

      return chips;
    },
  },
  mounted() {
    Promise.all([
      this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]),
      this.$store.dispatch('categories/getCategoriesList', [{ limit: CATEGORY_LIMIT }]),
      this.$store.dispatch('configs/getCustomerSegments'),
      this.$store.dispatch('agents/getAgentsSimpleList', [{ limit: AGENT_LIMIT }]),
      this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]),
      this.$store.dispatch('metrics/getFeedbackType'),
    ]).finally(() => {
      this.filterListsLoaded = true;
      this.applyFilterFromRoute();
    });
  },
  watch: {
    '$route.query': {
      handler() {
        if (this.filterListsLoaded) {
          this.applyFilterFromRoute();
        }
      },
    },
    opened: {
      immediate: true,
      handler(value) {
        this.isExpanded = value;
      },
    },
    shouldShowPrimaryAgent(show) {
      if (!show && this.filter.i_am_primary) {
        this.filter.i_am_primary = false;
      }
      if (!show && this.committedFilter.i_am_primary) {
        this.committedFilter.i_am_primary = false;
      }
    },
    'filter.agent_id'(newVal) {
      this.filter.agent_id = !newVal ? null : newVal;
    },
    'filter.team_id'(newVal) {
      this.filter.team_id = !newVal ? null : newVal;
    },
    'filter.last_feedback_score'(newVal) {
      this.filter.last_feedback_score = newVal === '' || newVal === undefined ? null : newVal;
    },
  },
  methods: {
    disableFutureDate,

    onStatusFilterChange(val, evt) {
      const currentButton = evt.target.defaultValue;
      if (!val) {
        this.$emit('select', []);
      } else {
        this.$emit('select', [currentButton]);
      }
    },

    collapsePanel() {
      this.isExpanded = false;
      this.$emit('open', false);
    },

    setAppliedFilter(filterState, { updateUrl = false, emitServer = false } = {}) {
      const normalized = coerceFilterState(filterState);
      this.committedFilter = cloneDeep(normalized);
      this.filter = cloneDeep(normalized);

      if (updateUrl) {
        this.syncFilterToUrl(normalized);
      }

      if (emitServer) {
        const payload = isFilterFilled(normalized, this.shouldShowPrimaryAgent)
          ? buildServerPayload(normalized)
          : {};
        this.$emit('applyFilter', payload);
      }
    },

    toggleExpanded() {
      const next = !this.isExpanded;
      this.isExpanded = next;
      this.$emit('open', next);
      this.filter = cloneDeep(this.committedFilter);
    },

    expandFilterPanel() {
      if (this.isExpanded) {
        return;
      }
      this.isExpanded = true;
      this.$emit('open', true);
      this.filter = cloneDeep(this.committedFilter);
    },

    onDateStartChange(newStartDate) {
      this.filter.dateRange.start_date = newStartDate ? toIsoString(capToNow(newStartDate)) : null;
    },

    onDateEndChange(newEndDate) {
      this.filter.dateRange.end_date = newEndDate ? toIsoString(newEndDate) : null;
    },

    clearFilter() {
      this.filter = cloneDeep(DEFAULT_FILTER);
    },

    syncFilterToUrl(filterState) {
      const filterQuery = buildUrlQueryFromFilterState(filterState);
      const currentFilterQuery = sanitizeAdvancedQueryParams(this.$route.query);

      if (!isEqual(currentFilterQuery, filterQuery)) {
        this.skipRouteFilterApply = true;
        const nextQuery = {
          ...stripAdvancedQueryParams(this.$route.query),
          ...filterQuery,
        };
        this.$router.push({ path: '/conversations', query: nextQuery })
          .finally(() => {
            this.$nextTick(() => {
              this.skipRouteFilterApply = false;
            });
          });
      }
    },

    applyFilterFromRoute() {
      if (this.skipRouteFilterApply) {
        return;
      }

      adjustUrlArray(this.$route.path, this.$route.query);
      adjustAdvancedUrlArrays(this.$route.query);

      if (!hasAdvancedFilterQueryParams(this.$route.query)) {
        if (!isAppliedFilterEmpty(this.appliedFilter)) {
          this.collapsePanel();
          this.setAppliedFilter(cloneDeep(DEFAULT_FILTER), { emitServer: true });
        }
        return;
      }

      const filterState = parseFilterFromUrlQuery(this.$route.query);
      const payload = isFilterFilled(filterState, this.shouldShowPrimaryAgent)
        ? buildServerPayload(filterState)
        : {};

      if (!areFiltersEqual(filterState, this.committedFilter)) {
        this.setAppliedFilter(filterState);
      }

      if (!areAppliedFiltersEqual(payload, this.appliedFilter)) {
        this.collapsePanel();
        this.$emit('applyFilter', payload);
      }
    },

    emitAppliedFilter(filterState) {
      this.setAppliedFilter(filterState, { updateUrl: true, emitServer: true });
    },

    removeAppliedFilter(key) {
      const updated = cloneDeep(this.committedFilter);

      switch (key) {
        case 'presets':
          PRESET_FILTER_KEYS.forEach((presetKey) => {
            updated[presetKey] = false;
          });
          break;
        case 'search':
          updated.searchType = DEFAULT_SEARCH.searchType;
          updated.searchValue = DEFAULT_SEARCH.searchValue;
          break;
        case 'dateRange':
          updated.dateRange = { start_date: null, end_date: null };
          break;
        case 'tag':
          updated.tag_id = null;
          break;
        case 'category':
          updated.category_id = null;
          break;
        case 'segments':
          updated.segment_ids = [];
          break;
        case 'agent':
          updated.agent_id = null;
          break;
        case 'team':
          updated.team_id = null;
          break;
        case 'lastFeedbackScore':
          updated.last_feedback_score = null;
          break;
        default:
          return;
      }

      this.filter = cloneDeep(updated);
      this.emitAppliedFilter(updated);
    },

    clearAllAppliedFilters() {
      const clearedFilter = cloneDeep(DEFAULT_FILTER);
      this.filter = cloneDeep(clearedFilter);
      this.emitAppliedFilter(clearedFilter);
    },

    onSearchEnter() {
      if (!this.isFilterUnchanged) {
        this.applyFilter();
      }
    },

    applyFilter() {
      this.collapsePanel();
      this.emitAppliedFilter(this.filter);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/aiq-variables.scss";
@import "../../../styles/aiq-mixins.scss";

.advanced-conversation-filter {
  padding: 2px;
  background-color: $aiq-bgc-body;

  &.is-expanded {
    flex: 0 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.status-filter-container {
  padding: 8px 8px 0 0;
  flex-shrink: 0;

  .el-checkbox-group {
    width: 216px;
    min-height: 30px;
  }
  :deep(.el-checkbox-button__inner) {
    font-size: 14px;
    height: auto;
    line-height: 16px;
    margin-bottom: 5px;
    padding: 5px 11px;
  }
}

.filter-block {
  border: 1px solid $aiq-bgc-conversation-filter-header;
  border-radius: 4px;
  overflow: hidden;

  &.is-expanded {
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 8px 12px;
  background-color: $aiq-bgc-conversation-filter-header;
  color: var(--aiq-color-white);
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  &:hover {
    background-color: $aiq-bgc-conversation-filter-header-hover;
  }
}

.filter-header-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
}

.filter-header-icon {
  font-size: 12px;
  transition: transform 300ms;

  &.expanded {
    transform: rotate(180deg);
  }
}

.filter-applied {
  padding: 8px 12px;
  background-color: var(--aiq-color-white);
}

.applied-filters-empty {
  font-size: 12px;
  line-height: 16px;
  color: $aiq-text-counters-filter;
}

.applied-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  line-height: 16px;
}

.applied-filters-title {
  color: #5e718a;
  font-weight: 400;
}

.applied-filters-link {
  color: #567db9;
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.applied-filter-chip {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 5px;
  border-radius: 4px;
  background-color: $aiq-bgc-conversations-filter;
  font-size: 12px;
  line-height: 16px;
  color: $aiq-text-counters-filter;

  &:last-child {
    margin-bottom: 0;
  }
}

.applied-filter-chip-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.applied-filter-chip-label {
  color: $aiq-text-counters-filter;
  font-weight: 400;
}

.applied-filter-chip-close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #5e718a;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;

  &:hover {
    color: #41474d;
  }
}

.filter-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background-color: var(--aiq-color-white);
  overflow: hidden;
}

.filter-form-fields {
  padding: 8px 12px 12px;
  overflow-y: auto;
  @include scroll-settings(6px, 1px, $aiq-bgc-scrollbar, 2px, $aiq-bgc-scrollbar);
}

.filter-field + .filter-field {
  margin-top: 12px;
}

.filter-field-label {
  display: block;
  margin-bottom: 8px;
  color: $aiq-text-counters-filter;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
}

.filter-select {
  width: 100%;
}

.filter-field-row {
  &:not(:last-child) {
    margin-bottom: 8px;
  }
}

.filter-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  padding: 12px 0px;
  margin: 0px 12px;
  border-top: 1px solid $aiq-color-border-medium;
}

.filter-action-button {
  min-width: 64px;
}

.no-search {
  padding: 0;
  background-color: var(--aiq-color-white);
}

.back-to-conversation {
  color: #567db9;
  cursor: pointer;
  height: 30px;
  display: flex;
  padding-top: 5px;

  span {
    margin: 3px 0 0 4px;
  }
}
</style>

<style lang="scss">
@import "../../../styles/aiq-variables.scss";

.advanced-conversation-filter {
  .conversations-date-picker {
    .el-input__inner {
      border: 1px solid $aiq-icon-primary !important;
    }
  }

  .filter-field-row {
    .el-date-editor.el-input {
      width: 100%;
      height: 30px;
    }
  }
}
</style>
